import { createContext } from 'preact';
import { useContext, useState, useEffect } from 'preact/hooks';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkSession();
  }, []);

  async function checkSession() {
    try {
      const sessionId = localStorage.getItem('sessionId');
      const workspaceId = localStorage.getItem('workspaceId');

      if (!sessionId || !workspaceId) {
        setLoading(false);
        return;
      }

      const response = await fetch('/api/auth/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, workspaceId })
      });

      if (response.ok) {
        // Safely parse JSON
        try {
          const text = await response.text();
          const data = text ? JSON.parse(text) : {};
          setUser(data.user);
          setWorkspace(data.workspace);
          setSubscription(data.subscription || null);
        } catch (parseError) {
          console.error('Failed to parse session response:', parseError);
          localStorage.removeItem('sessionId');
          localStorage.removeItem('workspaceId');
        }
      } else {
        localStorage.removeItem('sessionId');
        localStorage.removeItem('workspaceId');
      }
    } catch (error) {
      console.error('Session check failed:', error);
    } finally {
      setLoading(false);
    }
  }

  function login(sessionId, workspaceId) {
    localStorage.setItem('sessionId', sessionId);
    localStorage.setItem('workspaceId', workspaceId);
    checkSession();
  }

  function logout() {
    localStorage.removeItem('sessionId');
    localStorage.removeItem('workspaceId');
    setUser(null);
    setWorkspace(null);
    setSubscription(null);
  }

  return (
    <AuthContext.Provider value={{ user, workspace, subscription, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
